import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';

import { Tag, TagProps } from '@components/base';
import { Time } from '@enums/Time';
import { AvailabilityMap, getAvailability, isAvailable } from '@utils/availabilityUtils';
import { formatDate } from '@utils/dateUtils';

type DateValue = null | string;

type AvailabilityTagProps = Pick<TagProps, 'variant'> & {
  defaultAvailability?: Time;
  endTime?: DateValue;
  hideIfAlwaysAvailable?: boolean;
  hideIfAvailable?: boolean;
  hideTooltip?: boolean;
  /**
   * If `true`, the tag will have a shortened label
   */
  minified?: boolean;
  startTime?: DateValue;
};

export const AvailabilityTag: FC<AvailabilityTagProps> = ({
  defaultAvailability,
  endTime,
  hideIfAlwaysAvailable = false,
  hideIfAvailable = false,
  hideTooltip = false,
  minified,
  startTime,
  variant = 'outlined',
}) => {
  const { t } = useTranslation();
  const availability = getAvailability(startTime, endTime, defaultAvailability);
  const isItemAvailable = isAvailable(availability);
  if (hideIfAvailable && isItemAvailable) return null;
  if (hideIfAlwaysAvailable && availability === Time.ALWAYS) return null;

  const { icon, severity } = AvailabilityMap[availability];

  const tag = (
    <Tag
      color={severity}
      icon={icon}
      label={t(minified ? 'available' : 'available.description', {
        availableFrom: startTime ? formatDate(startTime) : undefined,
        availableTo: endTime ? formatDate(endTime) : undefined,
        context: availability,
      })}
      variant={variant}
    />
  );

  if (hideTooltip) return tag;

  return (
    <Tooltip
      title={
        startTime || endTime ? (
          <Stack>
            {startTime && (
              <Typography variant='inherit'>
                <Trans
                  components={{ strong: <strong /> }}
                  i18nKey={t('availableFromTime', { time: formatDate(startTime) })}
                />
              </Typography>
            )}

            {endTime && (
              <Typography variant='inherit'>
                <Trans
                  components={{ strong: <strong /> }}
                  i18nKey={t('availableToTime', { time: formatDate(endTime) })}
                />
              </Typography>
            )}
          </Stack>
        ) : (
          t('available.description', {
            context: availability,
            availableFrom: startTime ? formatDate(startTime) : undefined,
            availableTo: endTime ? formatDate(endTime) : undefined,
          })
        )
      }
    >
      {tag}
    </Tooltip>
  );
};
